function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { PageHeader, Ribbon, Empty } from "../../components/analytics-kit";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { Button } from "@progress/kendo-react-buttons";
import { num } from "../sales/territory-common";
import { kwService, fmtDate, isoDay, PositionChip, Chip } from "./common";
import { Link } from "react-router-dom";

/*
 * Keyword detail: per-engine rank history for one tracked keyword
 * (KEYWORD_INTELLIGENCE.md §6). Chart plots position over time with the
 * axis INVERTED (rank #1 at the top; up and to the right = winning).
 * Positions outside the top 100 are gaps, not zeros.
 */

const HISTORY_DAYS = 180;
const ENGINE_COLORS = {
  google: "#ff6358",
  bing: "#5b616b"
};
const ENGINE_NAMES = {
  google: "Google",
  bing: "Bing"
};
const engineName = e => ENGINE_NAMES[e] || (e ? e.charAt(0).toUpperCase() + e.slice(1) : "");

// Competition arrives as 0 to 1; say it plainly.
function crowded(c) {
  if (c == null) return "-";
  const v = Number(c);
  return v >= 0.67 ? "High" : v >= 0.34 ? "Medium" : "Low";
}
class KeywordDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "startEdit", () => {
      const k = this.state.data.keyword;
      this.setState({
        editing: {
          targetUrl: k.targetUrl || "",
          topicGroup: k.topicGroup || ""
        }
      });
    });
    _defineProperty(this, "save", async () => {
      const {
        data,
        editing,
        saving
      } = this.state;
      if (!editing || saving) return;
      const k = data.keyword;
      this.setState({
        saving: true
      });
      try {
        await kwService(`/${k.id}`).putAsync({
          term: k.term,
          engines: k.engines,
          active: k.active,
          targetUrl: editing.targetUrl.trim() || null,
          topicGroup: editing.topicGroup.trim() || null
        });
        this.setState({
          editing: null,
          saving: false,
          data: null
        });
        this.load();
      } catch (e) {
        this.setState({
          saving: false
        });
      }
    });
    this.state = {
      data: null,
      error: false,
      editing: null,
      saving: false
    };
    this._alive = true;
  }
  id() {
    return this.props.params && this.props.params.id;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    try {
      const [keywords, history, cannibal] = await Promise.all([kwService("").getAsync(), kwService(`/${this.id()}/history?days=${HISTORY_DAYS}`).getAsync(), kwService("/cannibalization").getAsync().catch(() => [])]);
      const keyword = (keywords || []).find(k => String(k.id) === String(this.id()));
      if (!keyword) {
        if (this._alive) this.setState({
          error: true
        });
        return;
      }
      const conflicts = (cannibal || []).filter(c => String(c.keywordId) === String(this.id()));
      if (this._alive) this.setState({
        data: {
          keyword,
          history: history || [],
          conflicts
        }
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  chartData(history) {
    if (!history || history.length === 0) return null;
    const engines = [...new Set(history.map(h => h.engine))].sort();
    // date → engine → position; sparse (missing = no check that day).
    const byDate = {};
    history.forEach(h => {
      const d = isoDay(h.checkedDate);
      (byDate[d] = byDate[d] || {})[h.engine] = h.position == null ? null : Number(h.position);
    });
    const dates = Object.keys(byDate).sort();
    if (dates.length < 2) return null; // a single point isn't a trend
    const rows = dates.map(d => [d, ...engines.map(e => e in byDate[d] ? byDate[d][e] : null)]);
    return {
      engines,
      table: [["Date", ...engines.map(engineName)], ...rows.map(([d, ...rest]) => [fmtDate(d), ...rest])]
    };
  }
  render() {
    const {
      data,
      error,
      editing,
      saving
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const {
      keyword: k,
      history,
      conflicts
    } = data;
    const chart = this.chartData(history);
    const recent = [...history].sort((a, b) => String(b.checkedDate).localeCompare(String(a.checkedDate))).slice(0, 30);
    const engines = String(k.engines || "google").split(",").map(x => x.trim()).filter(Boolean);
    const latestFor = e => recent.find(h => h.engine === e);
    const main = latestFor(engines.includes("google") ? "google" : engines[0]) || recent[0];
    const short = u => (u || "").replace(/^https?:\/\//, "");
    const sub = [engines.map(engineName).join(" and "), k.topicGroup ? `group: ${k.topicGroup}` : null, k.targetUrl ? `your page: ${short(k.targetUrl).slice(0, 60)}` : null].filter(Boolean).join(" · ");
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view keywords-view wa-view aps-root"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Apps"
      }, "Apps"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Keywords"
      }, "Keyword Intelligence")),
      title: k.term,
      subtitle: sub,
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, k.active === false ? /*#__PURE__*/React.createElement(Chip, {
        tone: "grey"
      }, "Paused") : null, /*#__PURE__*/React.createElement(Button, {
        onClick: this.startEdit
      }, "Edit"))
    }), editing && /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-add"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Edit term")), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement("input", {
      className: "wide",
      placeholder: "Page you want to rank (web address)",
      value: editing.targetUrl,
      onChange: e => this.setState({
        editing: {
          ...editing,
          targetUrl: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement("input", {
      placeholder: "Group name",
      value: editing.topicGroup,
      onChange: e => this.setState({
        editing: {
          ...editing,
          topicGroup: e.target.value
        }
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: saving,
      onClick: this.save
    }, saving ? "Saving…" : "Save"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        editing: null
      })
    }, "Cancel"))), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      tiles: [{
        l: "Your position",
        v: main ? main.position == null ? "Not in top 100" : `#${main.position}` : "-",
        s: main ? `${engineName(main.engine)}, ${fmtDate(main.checkedDate)}` : "not checked yet"
      }, {
        l: "Searches a month",
        v: k.searchVolume == null ? "-" : num(k.searchVolume)
      }, {
        l: "Cost per click",
        v: k.cpc == null ? "-" : `$${Number(k.cpc).toFixed(2)}`,
        s: "what advertisers pay"
      }, {
        l: "Competition",
        v: crowded(k.competition),
        s: "how many advertisers want it"
      }]
    }), conflicts.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "aps-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Pages getting in each other's way:"), " ", conflicts.map((c, i) => /*#__PURE__*/React.createElement("span", {
      key: i
    }, "on ", engineName(c.engine), " ", /*#__PURE__*/React.createElement("a", {
      href: c.rankedUrl,
      target: "_blank",
      rel: "noreferrer"
    }, short(c.rankedUrl).slice(0, 60)), " shows up", c.conflictType === "multi_url" ? " alongside other pages of yours" : ` instead of ${short(c.targetUrl).slice(0, 60)}`, i < conflicts.length - 1 ? "; " : "."))), /*#__PURE__*/React.createElement("section", {
      className: "aps-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Position over time"), /*#__PURE__*/React.createElement("span", null, chart ? `Last ${HISTORY_DAYS} days, ${history.length} checks. Higher on the chart is better; gaps mean you weren't in the top 100.` : null)), chart ? /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "320px",
      chartType: "LineChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chart.table,
      options: {
        series: chart.engines.reduce((acc, e, i) => {
          acc[i] = {
            color: ENGINE_COLORS[e] || "#ff6358"
          };
          return acc;
        }, {}),
        // Rank axis: #1 belongs at the TOP.
        vAxis: {
          direction: -1,
          title: "Position",
          format: "0",
          viewWindow: {
            min: 1
          }
        },
        interpolateNulls: false,
        pointSize: 4,
        legend: {
          position: "top"
        },
        backgroundColor: "transparent",
        chartArea: {
          left: 60,
          right: 30,
          top: 40,
          bottom: 40
        }
      }
    })) : /*#__PURE__*/React.createElement(Empty, null, "Not enough checks yet to show a trend. Each check adds a point.")), recent.length > 0 && /*#__PURE__*/React.createElement("section", {
      className: "aps-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Recent checks")), /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Date"), engines.length > 1 && /*#__PURE__*/React.createElement("th", null, "Search engine"), /*#__PURE__*/React.createElement("th", null, "Position"), /*#__PURE__*/React.createElement("th", null, "Page that shows up"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Searches a month"))), /*#__PURE__*/React.createElement("tbody", null, recent.map(h => /*#__PURE__*/React.createElement("tr", {
      key: String(h.id)
    }, /*#__PURE__*/React.createElement("td", null, fmtDate(h.checkedDate)), engines.length > 1 && /*#__PURE__*/React.createElement("td", null, engineName(h.engine)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(PositionChip, {
      position: h.position
    })), /*#__PURE__*/React.createElement("td", {
      className: "url",
      title: h.rankedUrl
    }, h.rankedUrl ? /*#__PURE__*/React.createElement("a", {
      href: h.rankedUrl,
      target: "_blank",
      rel: "noreferrer"
    }, short(h.rankedUrl)) : ""), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, h.searchVolume == null ? "" : num(h.searchVolume)))))))));
  }
}
export default withRouter(KeywordDetailViewComponent);